import { useMemo } from 'react';
import * as d3 from 'd3';

// Couleurs des cercles : la couleur choisie, sinon une teinte pastel dérivée du parent (comme la carte).
const pastelPalette = ['#A7C7E7', '#BDE0FE', '#CDB4DB', '#F9C6C9', '#FFE5B4', '#F7D9C4', '#B7E4C7', '#C3E5AE', '#D4F1F4', '#FDE2E4'];

export const circleColorPalette = [
  { value: '#A7C7E7', label: 'Bleu' },
  { value: '#BDE0FE', label: 'Ciel' },
  { value: '#CDB4DB', label: 'Lavande' },
  { value: '#F9C6C9', label: 'Rose' },
  { value: '#FFE5B4', label: 'Pêche' },
  { value: '#F7D9C4', label: 'Abricot' },
  { value: '#B7E4C7', label: 'Menthe' },
  { value: '#C3E5AE', label: 'Sauge' },
  { value: '#D4F1F4', label: 'Aqua' },
  { value: '#FDE2E4', label: 'Blush' },
];

const hashString = (value) => {
  const str = String(value || '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const keyOf = (circle) => (circle?.id !== undefined && circle?.id !== null ? `id:${circle.id}` : `name:${circle?.name || ''}`);

export const useCircleColors = (circles) =>
  useMemo(() => {
    const byId = new Map((circles || []).filter((c) => c?.id).map((c) => [Number(c.id), c]));
    const cache = new Map();
    const resolve = (circleId, visited = new Set()) => {
      if (!circleId) return null;
      const key = String(circleId);
      if (cache.has(key)) return cache.get(key);
      if (visited.has(key)) return null;
      visited.add(key);
      const circle = byId.get(Number(circleId));
      if (!circle) return null;
      let base = circle.color && d3.color(circle.color) ? circle.color : null;
      if (!base) {
        const seed = pastelPalette[hashString(keyOf(circle)) % pastelPalette.length];
        const parent = circle.parent_id ? resolve(circle.parent_id, visited) : null;
        if (parent) {
          const color = d3.hsl(parent);
          if (Number.isNaN(color.h)) {
            base = parent;
          } else {
            const h = hashString(keyOf(circle));
            color.h = (color.h + ((h % 11) - 5) + 360) % 360;
            color.s = clamp(color.s + 0.02, 0.25, 0.55);
            color.l = clamp(color.l + (((h >> 3) % 8) - 4) / 100, 0.78, 0.92);
            base = color.formatHex();
          }
        } else {
          base = seed;
        }
      }
      cache.set(key, base);
      return base;
    };
    const getMentionStyle = (mentionType, id) => {
      if (mentionType !== 'circle' && mentionType !== 'role') return null;
      const rgb = d3.color(resolve(id));
      if (!rgb) return null;
      const { r, g, b } = rgb.rgb();
      const mix = (v) => Math.round(v * 0.45);
      return {
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.22)`,
        borderColor: `rgba(${r}, ${g}, ${b}, 0.6)`,
        color: `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`,
      };
    };
    return { resolveColor: resolve, getMentionStyle };
  }, [circles]);
